/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { useContext, useRef } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { Provider } from 'react-aria-components/slots';
import { TextContext } from 'react-aria-components/Text';
import { ToggleButton } from 'react-aria-components/ToggleButton';
import { IconContext } from '../icon/context';
import { Tooltip } from '../tooltip';
import { TooltipTrigger } from '../tooltip/trigger';
import { SidenavContext } from './context';
import styles from './styles.module.css';
import type { SidenavItemProps } from './types';

/**
 * SidenavItem - Item component for sidenav
 *
 * Provides a selectable item with tooltip for the sidenav
 *
 * @example
 * ```tsx
 * <SidenavItem textValue="Dashboard" isSelected={true}>
 *   <Icon><Home /></Icon>
 *   <Text>Dashboard</Text>
 * </SidenavItem>
 * ```
 *
 * @param props - {@link SidenavItemProps}
 * @param props.children - Item content (icon and text).
 * @param props.classNames - Custom CSS class names for item elements.
 * @param props.textValue - Text displayed in tooltip when collapsed.
 * @returns The rendered SidenavItem component.
 */
export function SidenavItem({
  children,
  classNames,
  textValue,
  ...rest
}: SidenavItemProps) {
  const { isOpen } = useContext(SidenavContext);

  // Implement ref to place tooltip inside Button DOM to enable contextual styling
  const ref = useRef(null);

  return (
    <Provider
      values={[
        [IconContext, { size: 'medium' }],
        [TextContext, { className: clsx(styles.text, styles.transient) }],
      ]}
    >
      <TooltipTrigger isDisabled={isOpen}>
        <ToggleButton
          {...rest}
          ref={ref}
          className={composeRenderProps(classNames?.button, (className) =>
            clsx('group/item', styles.item, className),
          )}
        >
          {children}
        </ToggleButton>
        <Tooltip parentRef={ref} placement='right' className={styles.tooltip}>
          {textValue}
        </Tooltip>
      </TooltipTrigger>
    </Provider>
  );
}
